import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDashboardStats,
  getEmployees,
  getEmployeeDetails,
  verifyEmployee,
  rejectEmployee,
  getEmployers,
  getEmployerDetails,
  verifyEmployer,
  rejectEmployer,
  getAllJobs,
  getPendingJobs,
  getCompletedJobs,
  getJobDetails,
  getJobRoles,
  createJobRole,
  updateJobRole,
  deleteJobRole,
  getDocumentTypes,
  createDocumentType,
  updateDocumentType,
  deleteDocumentType,
  verifyDocument
} from '../services/adminService';
import { QUERY_KEYS } from '../utils/constants';
import { toast } from 'react-toastify';

// Dashboard
export const useDashboardStats = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.ADMIN_DASHBOARD],
    queryFn: getDashboardStats
  });
};

// Employees
export const useEmployees = (filters = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ADMIN_EMPLOYEES, filters],
    queryFn: () => getEmployees(filters)
  });
};

export const useEmployeeDetails = (id) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ADMIN_EMPLOYEE_DETAILS, id],
    queryFn: () => getEmployeeDetails(id),
    enabled: !!id
  });
};

export const useVerifyEmployee = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, comment }) => verifyEmployee(id, comment),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_EMPLOYEES]);
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_DASHBOARD]);
      toast.success('Employee verified successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to verify employee');
    }
  });
};

export const useRejectEmployee = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, comment }) => rejectEmployee(id, comment),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_EMPLOYEES]);
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_DASHBOARD]);
      toast.success('Employee rejected successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to reject employee');
    }
  });
};

// Employers
export const useEmployers = (filters = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ADMIN_EMPLOYERS, filters],
    queryFn: () => getEmployers(filters)
  });
};

export const useEmployerDetails = (id) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ADMIN_EMPLOYER_DETAILS, id],
    queryFn: () => getEmployerDetails(id),
    enabled: !!id
  });
};

export const useVerifyEmployer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, comment }) => verifyEmployer(id, comment),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_EMPLOYERS]);
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_DASHBOARD]);
      toast.success('Employer verified successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to verify employer');
    }
  });
};

export const useRejectEmployer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, comment }) => rejectEmployer(id, comment),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_EMPLOYERS]);
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_DASHBOARD]);
      toast.success('Employer rejected successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to reject employer');
    }
  });
};

// Jobs
export const useAllJobs = (filters = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ADMIN_JOBS, filters],
    queryFn: () => getAllJobs(filters)
  });
};

export const usePendingJobs = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.ADMIN_PENDING_JOBS],
    queryFn: getPendingJobs
  });
};

export const useCompletedJobs = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.ADMIN_COMPLETED_JOBS],
    queryFn: getCompletedJobs
  });
};

export const useAdminJobDetails = (id) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ADMIN_JOB_DETAILS, id],
    queryFn: () => getJobDetails(id),
    enabled: !!id
  });
};

// Job Roles
export const useJobRoles = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.ADMIN_JOB_ROLES],
    queryFn: getJobRoles
  });
};

export const useCreateJobRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createJobRole,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_JOB_ROLES]);
      toast.success('Job role created successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create job role');
    }
  });
};

export const useUpdateJobRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => updateJobRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_JOB_ROLES]);
      toast.success('Job role updated successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update job role');
    }
  });
};

export const useDeleteJobRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteJobRole,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_JOB_ROLES]);
      toast.success('Job role deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete job role');
    }
  });
};

// Document Types
export const useDocumentTypes = (filters = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ADMIN_DOCUMENTS, filters],
    queryFn: () => getDocumentTypes(filters)
  });
};

export const useCreateDocumentType = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createDocumentType,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_DOCUMENTS]);
      toast.success('Document type created successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create document type');
    }
  });
};

export const useUpdateDocumentType = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => updateDocumentType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_DOCUMENTS]);
      toast.success('Document type updated successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update document type');
    }
  });
};

export const useDeleteDocumentType = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteDocumentType,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_DOCUMENTS]);
      toast.success('Document type deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete document type');
    }
  });
};

// Document verification
export const useVerifyDocument = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ documentId, isVerified, userId }) => verifyDocument(documentId, isVerified),
    onSuccess: (data, variables) => {
      // Invalidate the specific user details query if userId is provided
      // The query key format used in useEmployeeDetails/useEmployerDetails is [QUERY_KEYS.ADMIN_EMPLOYEE_DETAILS, id]
      // where QUERY_KEYS.ADMIN_EMPLOYEE_DETAILS is a function that returns ['admin', 'employees']
      // So the actual key is [['admin', 'employees'], userId] or we need to call the function
      
      if (variables.userId) {
        // Invalidate using the function format
        queryClient.invalidateQueries({ 
          queryKey: QUERY_KEYS.ADMIN_EMPLOYEE_DETAILS(variables.userId)
        });
        queryClient.invalidateQueries({ 
          queryKey: QUERY_KEYS.ADMIN_EMPLOYER_DETAILS(variables.userId)
        });
        // Also invalidate the array format that's actually used in the hooks
        queryClient.invalidateQueries({ 
          queryKey: [QUERY_KEYS.ADMIN_EMPLOYEE_DETAILS, variables.userId]
        });
        queryClient.invalidateQueries({ 
          queryKey: [QUERY_KEYS.ADMIN_EMPLOYER_DETAILS, variables.userId]
        });
      }
      // Also invalidate all user details queries to ensure cache is refreshed
      queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'employers'] });
      
      toast.success(`Document ${variables.isVerified ? 'approved' : 'rejected'} successfully`);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update document status');
    }
  });
};










